import {
  BufferAttribute,
  BufferGeometry,
  Mesh,
  type MeshStandardMaterial,
  type Vector3,
} from 'three';
import {
  readEdges,
  readFaces,
  readSurfedges,
  readTexInfo,
  readVertexes,
  TOOL_SURF_MASK,
} from './lumps';
import type { BspReader } from './reader';
import { srcToWorld } from './transform';

/**
 * Visual geometry from the BSP face lump: each face is a polygon whose vertices
 * are walked through SURFEDGES → EDGES → VERTEXES (edge sign sets direction).
 * Faces flagged as tool surfaces (nodraw/sky/skip/hint/trigger) are skipped so we
 * render only what a player sees. This is render-only — collision comes from the
 * brushes (`brushGeometry.ts`). Textures are intentionally NOT imported (Valve
 * art is copyrighted + huge); the caller skins it with our materials.
 */
export function buildVisualMesh(reader: BspReader, material: MeshStandardMaterial): Mesh {
  const verts = readVertexes(reader);
  const edges = readEdges(reader);
  const surfedges = readSurfedges(reader);
  const faces = readFaces(reader);
  const texinfo = readTexInfo(reader);

  const positions: number[] = [];
  for (const face of faces) {
    if (face.dispinfo >= 0) continue; // displacement base quad — drawn by displacement.ts
    const ti = face.texinfo >= 0 ? texinfo[face.texinfo] : undefined;
    if (ti && (ti.flags & TOOL_SURF_MASK) !== 0) continue;
    if (face.numedges < 3) continue;

    const poly: Vector3[] = [];
    for (let i = 0; i < face.numedges; i++) {
      const se = surfedges[face.firstedge + i];
      if (se === undefined) continue;
      const edge = edges[Math.abs(se)];
      if (!edge) continue;
      const vi = se >= 0 ? edge[0] : edge[1];
      const v = verts[vi];
      if (!v) continue;
      poly.push(srcToWorld(v[0], v[1], v[2]));
    }
    if (poly.length < 3) continue;
    const a = poly[0] as Vector3;
    for (let i = 1; i + 1 < poly.length; i++) {
      const b = poly[i] as Vector3;
      const c = poly[i + 1] as Vector3;
      positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    }
  }

  const geom = new BufferGeometry();
  geom.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  geom.computeVertexNormals();
  const mesh = new Mesh(geom, material);
  mesh.name = 'bsp_visual';
  mesh.castShadow = false; // big imported maps — keep the shadow pass cheap
  mesh.receiveShadow = true;
  return mesh;
}

/** Triangle count of a built visual mesh (callers fall back to collision if 0). */
export function triangleCount(mesh: Mesh): number {
  const pos = mesh.geometry.getAttribute('position');
  return pos ? pos.count / 3 : 0;
}
